import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, AlertCircle, ArrowRight, Layers } from "lucide-react";

const BAND_STYLE = {
  low:  { label: "Low-level",  color: "bg-primary/10 text-primary border-primary/40" },
  mid:  { label: "Mid-level",  color: "bg-accent/15 text-accent border-accent/40" },
  high: { label: "High-level", color: "bg-primary text-primary-foreground border-primary" },
};

export default function Taxonomy() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/taxonomy").then((r) => setData(r.data)); }, []);
  if (!data) return <p className="p-8">Cargando taxonomía...</p>;
  return (
    <div className="space-y-8" data-testid="taxonomy-page">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-semibold">Marco clínico</p>
        <h1 className="heading text-4xl sm:text-5xl font-extrabold mt-1 tracking-tight">Áreas y errores</h1>
        <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
          11 áreas de trabajo desde <b>low-level</b> (percepción) hasta <b>high-level mentalizing</b>
          (metacognición), y 8 tipos de error clínico que guían el entrenamiento adaptativo.
        </p>
      </div>

      <section>
        <h2 className="heading text-xl font-bold flex items-center gap-2 mb-4">
          <Layers size={18} className="text-primary" /> 11 áreas de trabajo · Low → High mentalizing
        </h2>
        <div className="space-y-2" data-testid="domains-list">
          {data.domains.map((d) => {
            const b = BAND_STYLE[d.band] || BAND_STYLE.mid;
            return (
              <div key={d.key} className="rounded-xl border border-border bg-card p-4 flex items-start gap-4"
                   data-testid={`domain-${d.key}`}>
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0 heading font-extrabold">
                  {d.level}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="heading font-bold">{d.label}</span>
                    <Badge variant="outline" className={b.color}>{b.label}</Badge>
                  </div>
                  <p className="text-sm text-foreground/70 mt-1">{d.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>Low-level (percepción)</span>
          <ArrowRight size={14} />
          <span>Mid-level (comprensión)</span>
          <ArrowRight size={14} />
          <span>High-level (metacognición)</span>
        </div>
      </section>

      <section>
        <h2 className="heading text-xl font-bold flex items-center gap-2 mb-4">
          <AlertCircle size={18} className="text-accent" /> 8 tipos de error de mentalización
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="errors-list">
          {data.error_types.map((e) => (
            <Card key={e.key} className="rounded-xl border-border" data-testid={`error-${e.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Brain size={16} className="text-accent" />
                  <span className="heading font-bold">{e.label}</span>
                </div>
                <p className="text-sm text-foreground/80 mt-1">{e.description}</p>
                <p className="text-xs text-foreground/60 mt-2"><b>Ruta clínica:</b> {e.clinical_hint}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
